import { CommonModule } from '@angular/common';
import { Component, inject, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { FilterMatchMode, FilterMetadata, FilterService, MenuItem, MessageService, SelectItem, TreeNode } from 'primeng/api';
import { Breadcrumb } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { FloatLabel } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { ImageModule } from 'primeng/image';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { SplitButtonModule } from 'primeng/splitbutton';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { TreeSelectModule } from 'primeng/treeselect';

import { firstValueFrom, forkJoin } from 'rxjs';
import { SETTINGS, Settings } from '../../../../app.settings';
import { PrintLabelComponent, ProcessingComponent } from '../../../../components';

import {
  CatalogAdminService,
  IAppUser,
  ILocation,
  IOrderItemInstanceUpdateShippingRequest,
  InsectaryDashboardService,
  ISearchFilterValue,
  ISearchRequest,
  ISearchRequestFilter,
  ISearchResult,
  ISearchSort,
  OrderItemInstanceService,
  OrderService
} from '../../../../services/insecta';

import { afterRender } from '../../../../common/after-render';
import { notifyAndTrackError } from '../../../../common/error';
import { dateToIsoString, getItemDescriptionColumn, isNotEmpty, isStringEmpty, isStringEqual, toShortDate } from '../../../../common/functions';
import { successMsg, warningMsg } from '../../../../common/messages';
import { OrderItemInstanceStatus } from '../../../../services/insecta/insecta.models';

import { AppInitService, IInsectaPermissions } from '../../../../services/app-init.service';
import { AppInsightsService } from '../../../../services/app-insights.service';

@Component({
  selector: 'app-insectary-dashboard',
  standalone: true,
  imports: [
    CheckboxModule,
    CommonModule,
    ButtonModule,
    FileUploadModule,
    FormsModule,
    ImageModule,
    InputTextModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    RouterModule,
    TooltipModule,
    ProcessingComponent,
    PaginatorModule,
    InputNumberModule,
    DialogModule,
    DatePickerModule,
    Breadcrumb,
    FloatLabel,
    PrintLabelComponent,
    SplitButtonModule,
    TreeSelectModule
  ],
  templateUrl: './insect-prep.component.html'
})
export class InsectPrepComponent implements OnInit {
  @Input() appUser!: IAppUser;
  @ViewChild('dt') dt!: Table;

  userSettingsJson: { [key: string]: any } = {};
  route: any;
  permissions!: IInsectaPermissions;

  filtersActive: boolean = false;
  activeColumnFilters: Set<string> = new Set<string>();
  showSetupDateModel: boolean = false;
  showExportModel: boolean = false;
  setUpDate: Date | null = null;
  showPrintModal = false;
  showContainerQuantityModal: boolean = false;
  containerQuantity: number | null = null;
  selectedOrderItemInstanceIds: string[] = [];
  selectedOrderItemInstances: any[] = [];
  cols: any[] = [];

  customMatchModeOptions: SelectItem[] = [
    { label: 'Starts With', value: FilterMatchMode.STARTS_WITH },
    { label: 'Contains', value: FilterMatchMode.CONTAINS },
    { label: 'Not Contains', value: FilterMatchMode.NOT_CONTAINS },
    { label: 'Ends With', value: FilterMatchMode.ENDS_WITH },
    { label: 'Equals', value: FilterMatchMode.EQUALS },
    { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
    { label: 'Is Empty', value: 'isEmpty' },
    { label: 'Has Value', value: 'isNotEmpty' }
  ];
  dateMatchModeOptions: SelectItem[] = [
  { label: 'Date Is', value: FilterMatchMode.DATE_IS },
  { label: 'Date Is Not', value: FilterMatchMode.DATE_IS_NOT },
  { label: 'Date Before', value: FilterMatchMode.DATE_BEFORE },
  { label: 'Date After', value: FilterMatchMode.DATE_AFTER },
  { label: 'Is Empty', value: 'isEmpty' },
  { label: 'Has Value', value: 'isNotEmpty' }
];

numericMatchModeOptions: SelectItem[] = [
  { label: 'Equals', value: FilterMatchMode.EQUALS },
  { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
  { label: 'Less Than', value: FilterMatchMode.LESS_THAN },
  { label: 'Less Than or Equal', value: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
  { label: 'Greater Than', value: FilterMatchMode.GREATER_THAN },
  { label: 'Greater Than or Equal', value: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
  { label: 'Is Empty', value: 'isEmpty' },
  { label: 'Has Value', value: 'isNotEmpty' }
];
  deliverShipMenuItems: MenuItem[] = [];
  showDeliveryLocationDialog: boolean = false;
  deliveryLocation: TreeNode | undefined;
  deliveryLocations: TreeNode[] = [];
  isDeliverDialogValid: boolean = false;
  showShippingDialog: boolean = false;
  shippingInfo: IOrderItemInstanceUpdateShippingRequest = {
    instances: [],
    shippingVendor: null,
    shippingTrackingNumber: null,
    shippingDate: null
  };
  isShipDialogValid: boolean = false;

  private orderService: OrderService = inject(OrderService);

  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: false,
    pageId: `Dashboard:Insectary:InsectPrep`,
    breadcrumbItems: <MenuItem[]>[]
  };

  columns: string[] = [
    'OrderId',
    'ItemId',
    'ItemType',
    'InstanceId',
    'InstanceSequence',
    'DeliveryDate',
    'Insect',
    'GrowthStage',
    'ItemDescription',
    'ItemDescription_C',
    'ItemDescription_S',
    'ItemDescription_CS',
    'OrderItemInstanceQuantity',
    'ContainerQuantity',
    'ContainerName',
    'StandardFillAmount',
    'StandardInfestationRate',
    'InfestationRateQuantity',
    'PermitName',
    'Note',
    'RequesterName',
    'ProjectName',
    'InsectSetupDate',
    'DeliveryLocationName',
    'VendorDelay',
    'VendorDelayNote'
  ];

  selectAllToggle: boolean = false;
  orderItemInstances: any[] = [];
  orders: any[] = [];

  skip: number = 0;
  take: number = 10;
  totalCount: number = 0;

  insectaryDashboardService: InsectaryDashboardService = inject(InsectaryDashboardService);
  searchRequest: ISearchRequest = {
    columns: [],
    filters: [],
    sorts: [],
    skip: this.skip,
    take: this.take
  };

  itemDescName: string = '';
  siteName: string = '';

  constructor(
    @Inject(SETTINGS) private settings: Settings,
    private appInit: AppInitService,
    private appInsights: AppInsightsService,
    private msgService: MessageService,
    private orderItemInstanceService: OrderItemInstanceService,
    private catalogService: CatalogAdminService,
    private filterService: FilterService
  ) {
    this.pageVars.pageId = `${settings.environment}:Dashboard:InsectaryDashboard`;
    appInsights.trackPageView(this.pageVars.pageId, `/dashboard/insectary/insectprep`);
  }

  ngOnInit(): void {
    this.pageVars.isReady = false;
    this.appUser = this.appInit.appUser;
    this.permissions = this.appInit.permissions;

    this.filterService.register('isEmpty', (value: any): boolean => {
      return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
    });

    this.filterService.register('isNotEmpty', (value: any): boolean => {
      return value !== undefined && value !== null && (typeof value === 'string' && value.trim() !== '');
    });

    if (this.appUser) {
      if (!isStringEmpty(this.appUser.userSettingsJson) || this.appUser.userSettingsJson === '{}') {
        this.userSettingsJson = JSON.parse(this.appUser.userSettingsJson);
      } else {
        this.userSettingsJson = {};
      }

      this.itemDescName = getItemDescriptionColumn(this.userSettingsJson);
      this.siteName = this.userSettingsJson['Default Site'] || '';
    }

    this.initializeColumns();
    this.initializeDeliverShipMenu();

    if (this.permissions.canViewInsectaryDashboard && this.permissions.canViewRequestorDashboard) {
      this.pageVars.breadcrumbItems = [{ label: 'Insectary Dashboard', routerLink: '/insectarydashboard' }, { label: 'Insect Prep' }];
    } else {
      this.pageVars.breadcrumbItems = [{ label: 'Dashboard', routerLink: '/insectarydashboard' }, { label: 'Insect Prep' }];
    }
  }

  getColumnHeaderStyle(columnName: string): any {
    return this.activeColumnFilters.has(columnName) ? { 'background-color': '#e0f7fa', 'border-bottom': '2px solid #006064' } : {};
  }

  handleTableEvents(event: TableLazyLoadEvent) {
    this.pageVars.isReady = false;
    this.activeColumnFilters.clear();

    this.searchRequest.filters = [];
    this.searchRequest.filters.push(<ISearchRequestFilter>{
      fieldname: 'SiteName',
      logic: 'and',
      values: [<ISearchFilterValue>{ operator: 'equals', value: this.siteName }]
    });

    this.columns.forEach((columnName) => {
      const columnFilters = event.filters![columnName] as FilterMetadata[];
      if (columnFilters && columnFilters.length > 0) {
        const tableFilter = <ISearchRequestFilter>{
          fieldname: columnName,
          logic: columnFilters[0].operator,
          values: []
        };
        columnFilters.forEach((filterValue) => {
          if (filterValue.matchMode === 'isEmpty' || filterValue.matchMode === 'isNotEmpty') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode.toUpperCase(),
              value: ''
            });
          } else if (filterValue.value !== null && filterValue.value !== undefined && filterValue.value !== '') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode || `contains`,
              value: filterValue.matchMode?.startsWith(`date`) ? toShortDate(filterValue.value) : `${filterValue.value}`
            });
          }
        });
        if (tableFilter.values.length > 0) {
          this.searchRequest.filters.push(tableFilter);
          this.activeColumnFilters.add(columnName);
        }
      }
    });

    this.searchRequest.sorts = [];
    if (isNotEmpty(event.sortField)) {
      this.searchRequest.sorts.push(<ISearchSort>{ name: event.sortField, direction: event.sortOrder === 1 ? 'ASC' : 'DESC' });
    } else {
      this.searchRequest.sorts.push(<ISearchSort>{ name: 'DeliveryDate', direction: 'ASC' });
    }
    this.searchRequest.skip = event.first;
    this.searchRequest.take = event.rows ?? this.take;
    this.filtersActive = this.activeColumnFilters.size > 0 || isNotEmpty(event.sortField);
    this.executeSearch();
  }

  selectRowClick() {
    this.selectAllToggle = this.selectedOrderItemInstances.length == this.totalCount;
  }

  toggleSelectAll(checkAll: boolean) {
    if (!checkAll) {
      this.selectedOrderItemInstances = [];
      this.selectAllToggle = false;
      return;
    }

    this.pageVars.isReady = false;

    const selectAllSearch = this.searchRequest;
    selectAllSearch.columns = ['InstanceId'];
    selectAllSearch.skip = 0;
    selectAllSearch.take = this.totalCount;

    firstValueFrom(this.insectaryDashboardService.getInsectPrepTable(selectAllSearch))
      .then((response: ISearchResult) => {
        const results = JSON.parse(response?.results ?? `[]`);
        this.selectedOrderItemInstances = results.map((x: any) => x.InstanceId);
        this.selectAllToggle = true;
      })
      .catch((err) => {
        this.selectedOrderItemInstances = [];
        this.selectAllToggle = false;
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: this.pageVars.pageId, action: 'SelectAll' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  executeSearch() {
    const request = this.searchRequest;
    request.columns = [...this.columns, this.itemDescName];

    firstValueFrom(this.insectaryDashboardService.getInsectPrepTable(request))
      .then(async (response) => {
        this.totalCount = response?.totalCount || 0;
        this.orderItemInstances = JSON.parse(response?.results ?? `[]`);
        this.selectedOrderItemInstanceIds = [];
        this.selectedOrderItemInstances = [];
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectPrepComponent', action: 'ExecuteSearch' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  addSetupDate() {
    this.showSetupDateModel = true;
  }

  onSaveSetupDate() {
    if (this.selectedOrderItemInstances.length > 0) {
      this.pageVars.isReady = false;

      const selectedIds = new Set(this.selectedOrderItemInstances);
      const selectedInstances = this.orderItemInstances.filter((instance: any) => selectedIds.has(instance.InstanceId));
      const instanceArgs = selectedInstances.map(
        (selectedInstance) =>
          <any>{
            id: selectedInstance.InstanceId,
            orderId: selectedInstance.OrderId,
            orderItemId: selectedInstance.ItemId,
            substrateSetupDate: null,
            insectSetupDate: this.setUpDate ? dateToIsoString(this.setUpDate) : null,
            itemType: selectedInstance.ItemType
          }
      );
      const requests = instanceArgs.map((args) =>
        this.orderItemInstanceService.addSetupDateOrderItemInstance(args.orderId, args.orderItemId, args.id, args)
      );

      firstValueFrom(forkJoin(requests))
        .then(() => {
          successMsg(this.msgService, 'Set up date added successfully');
          this.executeSearch();
        })
        .catch((err) => {
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'SubstratePrepComponent', action: 'AddSetupDate' });
        })
        .finally(() => {
          this.pageVars.isReady = true;
        });
    } else {
      warningMsg(this.msgService, 'No order item instance selected');
    }
    this.showSetupDateModel = false;
  }

  onCancelSetupDate() {
    this.showSetupDateModel = false;
  }

  async exportThisList() {
    this.pageVars.isReady = false;

    try {
      const exportRequest = this.searchRequest;
      const fixedColumnOrder = [
        'InstanceSequence',
        'DeliveryDate',
        'RequesterName',
        'Note',
        this.itemDescName,
        'OrderItemInstanceQuantity',
        'ContainerQuantity',
        'ContainerName',
        'InfestationRateQuantity',
        'InsectSetupDate',
        'DeliveryLocationName',
        'VendorDelay',
        'VendorDelayNote'
      ];

      exportRequest.columns = fixedColumnOrder;
      exportRequest.skip = 0;
      exportRequest.take = this.totalCount;
      exportRequest.sorts = [];
      await this.insectaryDashboardService.getInsectPrepExport(exportRequest);
    } catch (err) {
      notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectPrepComponent', action: 'ExportInstances' });
    } finally {
      this.pageVars.isReady = true;
    }
  }

  initializeDeliverShipMenu() {
    this.deliverShipMenuItems = [
      {
        label: 'Add Delivery Location',
        icon: 'pi pi-map-marker',
        command: () => this.showDeliveryLocationDlg()
      },
      {
        label: 'Add Shipping Info',
        icon: 'pi pi-truck',
        command: () => this.showShippingDlg()
      },
      {
        label: 'Mark as Delivered',
        icon: 'pi pi-check',
        command: () => this.markAsDelivered()
      }
    ];
  }

  showDeliveryLocationDlg() {
    if (this.selectedOrderItemInstances.length === 0) {
      warningMsg(this.msgService, 'No order item instance selected');
      return;
    }

    this.pageVars.isReady = false;

    firstValueFrom(this.orderService.getLocationsForHierarchy())
      .then((response) => {
        const siteLocations = response.find((x) => isStringEqual(x.name, this.siteName))?.children ?? [];
        this.deliveryLocation = undefined;
        this.deliveryLocations = siteLocations.map((x) => this.buildLocationNode(x));
        this.showDeliveryLocationDialog = true;
        this.computeDeliverDialogValidation(false);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectPrepComponent', action: 'LoadLocations' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  buildLocationNode(location: ILocation): TreeNode {
    location.locationTypeId = location.locationTypeId.toUpperCase();
    location.containmentTypeId = location.containmentTypeId?.toUpperCase();
    if (isNotEmpty(location.address)) {
      location.address!.privateUserId = location.address!.privateUserId?.toUpperCase();
    }
    return <TreeNode>{
      key: location.id,
      label: location.name,
      data: location,
      children: location.children.map((x) => this.buildLocationNode(x))
    };
  }

  computeDeliverDialogValidation(showToast: boolean): boolean {
    this.isDeliverDialogValid = this.deliveryLocation !== undefined;
    if (!this.isDeliverDialogValid && showToast) {
      warningMsg(this.msgService, 'Please select a delivery location');
    }
    return this.isDeliverDialogValid;
  }

  cancelDeliveryLocationDlg() {
    this.showDeliveryLocationDialog = false;
    this.deliveryLocation = undefined;
  }

  saveDeliveryLocation() {
    if (!this.computeDeliverDialogValidation(true)) {
      return;
    }
    this.pageVars.isReady = false;

    const instanceIds = this.selectedOrderItemInstances;

    firstValueFrom(this.orderItemInstanceService.updateOrderItemInstanceDeliveryLocation(instanceIds, (this.deliveryLocation as any).data.id))
      .then(() => {
        this.selectedOrderItemInstances = [];
        this.selectAllToggle = false;
        this.showDeliveryLocationDialog = false;
        this.executeSearch();
        this.computeDeliverDialogValidation(false);
        successMsg(this.msgService, 'Delivery location updated successfully');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectPrepComponent', action: 'SaveDeliveryLocation' });
        this.pageVars.isReady = true;
      });
  }

  showShippingDlg() {
    if (this.selectedOrderItemInstances.length === 0) {
      warningMsg(this.msgService, 'No order item instance selected');
      return;
    }

    this.shippingInfo = {
      instances: this.selectedOrderItemInstances,
      shippingVendor: null,
      shippingTrackingNumber: null,
      shippingDate: null
    };

    this.showShippingDialog = true;
    this.computeShipDialogValidation(false);
  }

  computeShipDialogValidation(showToast: boolean): boolean {
    const hasVendor = this.shippingInfo.shippingVendor !== null && this.shippingInfo.shippingVendor !== '';
    const hasTracking = this.shippingInfo.shippingTrackingNumber !== null && this.shippingInfo.shippingTrackingNumber !== '';
    const hasDate = this.shippingInfo.shippingDate !== null;

    this.isShipDialogValid = hasVendor && hasTracking && hasDate;

    if (!this.isShipDialogValid && showToast) {
      warningMsg(this.msgService, 'Please fill in all shipping information');
    }
    return this.isShipDialogValid;
  }

  onShippingVendorChange() {
    this.computeShipDialogValidation(false);
  }

  onShippingTrackingChange() {
    this.computeShipDialogValidation(false);
  }

  onShippingDateChange() {
    this.computeShipDialogValidation(false);
  }

  cancelShippingDlg() {
    this.showShippingDialog = false;
    this.shippingInfo = {
      instances: [],
      shippingVendor: null,
      shippingTrackingNumber: null,
      shippingDate: null
    };
  }

  saveShippingInfo() {
    if (!this.computeShipDialogValidation(true)) {
      return;
    }
    this.pageVars.isReady = false;

    this.shippingInfo.instances = this.selectedOrderItemInstances;
    this.shippingInfo.shippingDate = dateToIsoString(this.shippingInfo.shippingDate as unknown as Date);

    firstValueFrom(this.orderItemInstanceService.updateOrderItemInstanceShipping(this.shippingInfo))
      .then(() => {
        this.selectedOrderItemInstances = [];
        this.selectAllToggle = false;
        this.showShippingDialog = false;
        successMsg(this.msgService, 'Shipping information updated successfully');
        this.executeSearch();
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectPrepComponent', action: 'SaveShipping' });
        this.pageVars.isReady = true;
      });
  }

  markAsDelivered() {
    if (this.selectedOrderItemInstances.length === 0) {
      warningMsg(this.msgService, 'No order item instance selected');
      return;
    }

    this.pageVars.isReady = false;
    firstValueFrom(this.orderItemInstanceService.updateOrderItemInstanceWorkflowStatus(this.selectedOrderItemInstances, OrderItemInstanceStatus.Delivered))
      .then(() => {
        this.selectedOrderItemInstances = [];
        this.selectAllToggle = false;
        successMsg(this.msgService, 'Instances marked as delivered successfully');
        this.executeSearch();
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectPrepComponent', action: 'MarkAsDelivered' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  printLabels() {
    if (this.selectedOrderItemInstances.length > 0) {
      this.pageVars.isReady = false;

      firstValueFrom(this.catalogService.getBartenderConfig())
        .then((config) => {
          const hasServerUrl = config.serverUrl && config.serverUrl.trim() !== '';
          if (!hasServerUrl) {
            warningMsg(this.msgService, 'Printer setup has not been configured. Please contact your administrator.');
            return;
          }
          this.selectedOrderItemInstanceIds = this.selectedOrderItemInstances.filter(
            (id) => id != null && id !== '' && id !== '00000000-0000-0000-0000-000000000000'
          );

          if (this.selectedOrderItemInstanceIds.length === 0) {
            warningMsg(this.msgService, 'Selected items do not have valid instance IDs.');
            return;
          }

          afterRender(() => {
            this.showPrintModal = true;
          });
        })
        .catch((err) => {
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectPrepComponent', action: 'PrintLabelsConfig' });
        })
        .finally(() => {
          this.pageVars.isReady = true;
        });
    } else {
      warningMsg(this.msgService, 'No order item instance selected.');
    }
  }

  updateContainerQuantity() {
    this.containerQuantity = null;
    this.showContainerQuantityModal = true;
  }

  onSaveContainerQuantity() {
    if (this.selectedOrderItemInstances.length > 0 && this.containerQuantity !== null) {
      this.pageVars.isReady = false;
      const selectedObjects = this.orderItemInstances.filter((instance) => this.selectedOrderItemInstances.includes(instance.InstanceId));
      selectedObjects.forEach((selectedInstance) => {
        const orderId = selectedInstance.OrderId;
        const id = selectedInstance.InstanceId;
        const updateRequest = {
          id: id,
          OrderId: selectedInstance.OrderId,
          ContainerQuantity: this.containerQuantity
        };
        firstValueFrom(this.orderItemInstanceService.updateContainerQuantity(orderId, id, updateRequest))
          .then(() => {
            this.pageVars.isReady = true;
            successMsg(this.msgService, 'Container quantity updated successfully');
            this.executeSearch();
          })
          .catch((err) => {
            notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectPrepComponent', action: 'UpdateContainerQuantity' });
          });
      });
    } else {
      warningMsg(this.msgService, 'Please enter a valid quantity');
    }
    this.showContainerQuantityModal = false;
  }

  onCancelContainerQuantity() {
    this.showContainerQuantityModal = false;
    this.containerQuantity = null;
  }

  afterPrintLabels() {
    this.pageVars.isReady = true;
  }

  clearFilters(table: Table) {
    table.sortField = null;
    table.sortOrder = 1;
    table.clear();
    if (table.filters) {
    Object.keys(table.filters).forEach((key) => {
      const col = this.cols?.find((c: any) => c.field === key);
      const filters = table.filters[key] as any[];
      if (Array.isArray(filters)) {
        filters.forEach((f) => {
          if (f.matchMode === 'isEmpty' || f.matchMode === 'isNotEmpty') {
          if (col?.type === 'date') {
            f.matchMode = 'dateIs';
          } else if (col?.type === 'numeric') {
            f.matchMode = 'equals';
          } else {
            f.matchMode = 'contains';
          }
            f.value = null;
          }
        });
      }
    });
  }
    this.activeColumnFilters.clear();
    this.filtersActive = false;

    try {
      const stateKey = 'insect-prep-table';
      const storedState = localStorage.getItem(stateKey);
      if (storedState) {
        const state = JSON.parse(storedState);
        delete state.sortField;
        delete state.sortOrder;
        delete state.filters;
        localStorage.setItem(stateKey, JSON.stringify(state));
      }
    } catch (e) {
      console.error('Error updating local storage in clearFilters', e);
    }

    setTimeout(() => {
      this.searchRequest.filters = [];
      this.searchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'SiteName',
        logic: 'and',
        values: [<ISearchFilterValue>{ operator: 'equals', value: this.siteName }]
      });
      this.searchRequest.sorts = [{ name: 'DeliveryDate', direction: 'ASC' }];
      this.searchRequest.skip = 0;
      this.searchRequest.take = this.take;
      this.executeSearch();
    }, 100);
  }

  resetPage(table: Table) {
    table.clear();
    table.clearState();
    window.location.reload();
  }

  initializeColumns() {
    const descField = this.itemDescName || 'ItemDescription';
    this.cols = [
      { field: 'InstanceSequence', header: 'Order Item Instance ID', type: 'text', template: 'link' },
      { field: 'DeliveryDate', header: 'Delivery Date', type: 'date' },
      { field: 'RequesterName', header: 'Requester', type: 'text' },
      { field: 'Note', header: 'Note', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'ItemDescription', header: 'Item Description', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'OrderItemInstanceQuantity', header: 'Insect Quantity', type: 'numeric' },
      { field: 'ContainerQuantity', header: 'Container Quantity', type: 'numeric' },
      { field: 'ContainerName', header: 'Container', type: 'text', template: 'tooltip' },
      { field: 'InfestationRateQuantity', header: 'Infestation Rate', type: 'numeric' },
      { field: 'InsectSetupDate', header: 'Set-Up Date', type: 'date' },
      { field: 'DeliveryLocationName', header: 'Delivery Location', type: 'text' },
      { field: 'VendorDelay', header: 'Vendor Delay (yes/no)', type: 'boolean', template: 'boolean', headerStyle: { 'min-width': '200px' } },
      { field: 'VendorDelayNote', header: 'Vendor Delay Note', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } }
    ];

    this.syncColumnState(descField);
  }

  private syncColumnState(variantField: string) {
    const stateKey = 'insect-prep-table';
    try {
      const storedState = localStorage.getItem(stateKey);
      if (!storedState) return;

      const state = JSON.parse(storedState);
      const columnOrder = state?.columnOrder as string[] | undefined;

      if (columnOrder && !columnOrder.includes(variantField)) {
        delete state.columnOrder;
        localStorage.setItem(stateKey, JSON.stringify(state));
      }
    } catch (e) {
      console.error('Error updating local storage column order', e);
    }
  }
}